/**
 * Common Utilities for Clinic App
 * Shared functions used across multiple pages
 */

// Initialize global namespace
window.ClinicApp = window.ClinicApp || {};

/**
 * API Helper Methods
 */
window.ClinicApp.Api = {
    /**
     * Fetch doctors with optional search and pagination
     * @param {Object} options - Options for fetching doctors
     * @param {string} options.search - Search term (optional)
     * @param {number} options.page - Page number (default: 1)
     * @param {number} options.pageSize - Page size (default: 100)
     * @returns {Promise<Array>} Array of doctor objects
     */
    async getDoctors(options = {}) {
        const {
            search = '',
            page = 1,
            pageSize = 100
        } = options;

        try {
            const params = new URLSearchParams({
                search: search,
                page: page.toString(),
                pageSize: pageSize.toString()
            });

            const response = await fetch(`/appointments/search-doctors?${params.toString()}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.success}`);
            }

            const data = await response.json();
            
            // Handle different response formats
            if (Array.isArray(data)) {
                return data;
            } else if (data.doctors && Array.isArray(data.doctors)) {
                return data.doctors;
            } else {
                console.warn('Unexpected response format:', data);
                return [];
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
            Toast.error('Failed to load doctors');
            return [];
        }
    },

    /**
     * Fetch a single doctor by ID
     * @param {string} doctorId - Doctor GUID
     * @returns {Promise<Object|null>} Doctor object or null
     */
    async getDoctorById(doctorId) {
        try {
            const response = await fetch(`/doctors/api/details/${doctorId}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching doctor:', error);
            return null;
        }
    },

    /**
     * Fetch patients with optional search and pagination
     * @param {Object} options - Options for fetching patients
     * @param {string} options.search - Search term (optional)
     * @param {number} options.page - Page number (default: 1)
     * @param {number} options.pageSize - Page size (default: 20)
     * @returns {Promise<Object>} Response with patients array and metadata
     */
    async getPatients(options = {}) {
        const {
            search = '',
            page = 1,
            pageSize = 20
        } = options;

        try {
            const params = new URLSearchParams({
                search: search,
                page: page.toString(),
                pageSize: pageSize.toString()
            });

            const response = await fetch(`/patients/api/search?${params.toString()}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching patients:', error);
            Toast.error('Failed to load patients');
            return { items: [], totalCount: 0 };
        }
    }
};

/**
 * Formatting Helper Methods
 */
window.ClinicApp.Format = {
    /**
     * Format currency amount
     * @param {number} amount - Amount to format
     * @param {string} currency - Currency symbol (default: ₹)
     * @returns {string} Formatted currency string
     */
    currency(amount, currency = '₹') {
        if (amount === null || amount === undefined) {
            return `${currency}0`;
        }
        return currency + amount.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    },

    /**
     * Format date string
     * @param {string|Date} dateString - Date to format
     * @param {Object} options - Intl.DateTimeFormat options
     * @returns {string} Formatted date string
     */
    date(dateString, options = {}) {
        if (!dateString) return 'N/A';
        
        const defaultOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        };

        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
    },

    /**
     * Format time string
     * @param {string|Date} dateString - Date to format
     * @returns {string} Formatted time string
     */
    time(dateString) {
        if (!dateString) return 'N/A';
        
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    },

    /**
     * Format phone number
     * @param {string} phone - Phone number
     * @returns {string} Formatted phone number
     */
    phone(phone) {
        if (!phone) return '';
        // Add custom phone formatting logic here
        return phone;
    }
};

/**
 * Validation Helper Methods
 */
window.ClinicApp.Validate = {
    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} True if valid
     */
    email(email) {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    },

    /**
     * Validate phone number
     * @param {string} phone - Phone to validate
     * @returns {boolean} True if valid
     */
    phone(phone) {
        // Basic validation - adjust pattern as needed
        const regex = /^[\d\s\-\+\(\)]{10,}$/;
        return regex.test(phone);
    },

    /**
     * Validate required field
     * @param {any} value - Value to check
     * @returns {boolean} True if not empty
     */
    required(value) {
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        return value !== null && value !== undefined && value !== '';
    }
};

/**
 * DOM Helper Methods
 */
window.ClinicApp.Dom = {
    /**
     * Get CSRF token from page
     * @returns {string|null} CSRF token value
     */
    getCsrfToken() {
        const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]');
        return tokenInput ? tokenInput.value : null;
    },

    /**
     * Show loading spinner
     * @param {HTMLElement} element - Element to show spinner in
     */
    showLoading(element) {
        if (element) {
            element.innerHTML = `
                <div class="text-center py-3">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            `;
        }
    },

    /**
     * Disable form while submitting
     * @param {HTMLFormElement} form - Form element
     * @param {boolean} disabled - Whether to disable
     */
    disableForm(form, disabled = true) {
        if (!form) return;

        const inputs = form.querySelectorAll('input, select, textarea, button');
        inputs.forEach(input => {
            input.disabled = disabled;
        });
    }
};

/**
 * Appointment Status Management
 */
window.ClinicApp.Appointment = {
    /**
     * Status transition rules mapping
     * Defines which statuses can transition to which other statuses
     */
    statusTransitionMap: {
        'scheduled': ['confirmed', 'arrived', 'completed', 'cancelled', 'no_show'],
        'booked': ['confirmed', 'arrived', 'completed', 'cancelled', 'no_show'],
        'confirmed': ['arrived', 'completed', 'cancelled', 'no_show'],
        'arrived': ['completed', 'no_show', 'cancelled'],
        'completed': [],
        'cancelled': [],
        'no_show': [],
        'no-show': []
    },

    /**
     * Available status options for dropdown display
     */
    statusOptions: [
        { value: 'confirmed', label: 'Confirmed', icon: 'bi-check-circle', class: 'text-success' },
        { value: 'arrived', label: 'Arrived', icon: 'bi-door-open', class: 'text-info' },
        { value: 'completed', label: 'Completed', icon: 'bi-check-all', class: 'text-info' },
        { value: 'cancelled', label: 'Cancel', icon: 'bi-x-circle', class: 'text-danger' },
        { value: 'no_show', label: 'No Show', icon: 'bi-exclamation-triangle', class: 'text-warning' }
    ],

    /**
     * Get allowed status transitions for the current status
     * @param {string} currentStatus - The current appointment status
     * @returns {Array} Array of allowed status values
     */
    getAllowedStatusTransitions(currentStatus) {
        const normalized = currentStatus ? currentStatus.toLowerCase().replace(' ', '-') : '';
        return this.statusTransitionMap[normalized] || [];
    },

    /**
     * Get status options filtered by allowed transitions
     * @param {string} currentStatus - The current appointment status
     * @returns {Array} Filtered status options with value, label, icon, and class
     */
    getAvailableStatusOptions(currentStatus) {
        const allowedStatuses = this.getAllowedStatusTransitions(currentStatus);
        return this.statusOptions.filter(option => allowedStatuses.includes(option.value));
    },

    /**
     * Check if a status is a terminal state (no further transitions allowed)
     * @param {string} status - The status to check
     * @returns {boolean} True if status is terminal
     */
    isTerminalStatus(status) {
        const allowed = this.getAllowedStatusTransitions(status);
        return allowed.length === 0;
    },

    /**
     * Get only regular status options (excluding terminals)
     * @param {string} currentStatus - The current appointment status
     * @returns {Array} Filtered regular status options
     */
    getRegularStatusOptions(currentStatus) {
        return this.getAvailableStatusOptions(currentStatus)
            .filter(o => o.value !== 'cancelled' && o.value !== 'no_show');
    },

    /**
     * Get only terminal status options
     * @param {string} currentStatus - The current appointment status
     * @returns {Array} Filtered terminal status options
     */
    getTerminalStatusOptions(currentStatus) {
        return this.getAvailableStatusOptions(currentStatus)
            .filter(o => o.value === 'cancelled' || o.value === 'no_show');
    }
};

// No need to expose again - already on window.ClinicApp

